import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import _, { find, forEach } from 'lodash';
import SearchBarPopover from '../SearchBarPopover';
import {
    getPersistedState,
    setPersistedState,
} from '../../actions/persistedStateActions';
import {
    getChildDropdownOptions, getDropdownOptionsByVertical,
    getServicesLabels,
    getWealthDropdownOptions,
} from '../../data/getDropdownOptions';
import { searchFiltersGTM, searchFiltersCheckboxGTM } from '../../data/analyticsHelper';
import { getWealthLevelIds } from '../../data/attributeHelper';
import {
    calculateMortgageAdviserFilterState,
    getParentListStateForPensionTransfersService,
} from '../../data/searchHelper';
import { createInputId } from '../../utils/utilities';
import {
    calculateIfMortgageAdviserSearch,
    getSearchFilterTypeValueByService,
} from '../../utils/searchUtils';
import {
    FINANCIAL_ADVISER_ID,
    LEGAL_ADVISER_ID,
    ACCOUNTANT_ID,
    PERSISTED_STATE_SEARCH_FILTERS,
    FAKE_FINANCIAL_ADVISER_PENSION_TRANSFERS_DROPDOWN_ID,
    PERSISTED_STATE_SEARCH_PAGE,
} from '../../__constants__';

import servicesAccountant from '../../data/services-accountant';
import servicesFinancial from '../../data/services-financial-adviser';
import servicesLegal from '../../data/services-legal-adviser';

import Select from '../Inputs/Select';
import Button from '../../components/Inputs/Button';
import Checkbox from '../Inputs/Checkbox';
import Drawer from '../Drawer'
import Tune from '../vectors/Tune';
import FilterServicesDropdown from '../Search/FilterServicesDropdown';
import Tooltip from '../Tooltip'
import SelectOption from "../Inputs/SelectOption";

const propTypes = {
    location: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
    handleSubmit: PropTypes.func.isRequired,
};

const services = {
    [FINANCIAL_ADVISER_ID]: servicesFinancial.financial_adviser_services,
    [ACCOUNTANT_ID]: servicesAccountant.accountant_business_services,
    [LEGAL_ADVISER_ID]: servicesLegal.legal_adviser_services,
};

export const SEARCH_FILTER_SERVICES_WRAPPER = 'searchFilterServicesWrapper';

const searchRangeOptions = [
    {value: 5, label: 'Up to 5 miles'},
    {value: 10, label: 'Up to 10 miles'},
    {value: 15, label: 'Up to 15 miles'},
    {value: 20, label: 'Up to 20 miles'},
    {value: 25, label: 'Up to 25 miles'},
    {value: 30, label: 'Up to 30 miles'},
    {value: 35, label: 'Up to 35 miles'},
    {value: 40, label: 'Up to 40 miles'},
    {value: 45, label: 'Up to 45 miles'},
    {value: 50, label: 'Up to 50 miles'},
    {value: 55, label: 'Up to 55 miles'},
    {value: 60, label: 'Up to 60 miles'},
    {value: 65, label: 'Up to 65 miles'},
    {value: 70, label: 'Up to 70 miles'},
    {value: 75, label: 'Up to 75 miles'},
    {value: 80, label: 'Up to 80 miles'},
];

export const genderOptions = [
    {value: 'null', label:'All'},
    {value: 'm', label:'Men'},
    {value: 'f', label:'Women'},
    {value: 'nb', label:'Non-binary'},
]

export function SearchFilters(props) {
    const [showFilters, setShowFilters] = useState(false)
    const [mobileFilters, setMobileFilters] = useState(false)
    const [mortgageAdviser, setMortgageAdviser] = useState(false)
    const [showAdviceAreas, setShowAdviceAreas] = useState(props.type !== FINANCIAL_ADVISER_ID)
    const [showSearchBarPopover, setShowSearchBarPopover] = useState(false)
    const didMountRef = useRef(false)
    const [width, setWidth] = useState(0);
    const [filters, setFilters] = useState({
        wealth: null,
        search_range: 40,
        is_contactable: props.type !== LEGAL_ADVISER_ID,
        promotion: false,
        include_travel_professionals: true,
        type: props.type,
        type_filter: false,
        type_filter_value: 'ifa',
        parentList: [],
        servicesList: [],
        wealth_level_ids: null,
        advanced_qualifications: false,
        fixed_fees: false,
        gender: null,
    })

    const { urlPath, type, attribute_id, classes, location } = props

    useEffect(() => {
        const persistedSearchPage = getPersistedState(PERSISTED_STATE_SEARCH_PAGE);
        const persistedSearchFilters = getPersistedState(PERSISTED_STATE_SEARCH_FILTERS);
        if (!persistedSearchPage || !persistedSearchFilters) {
            addUrlParamSearchFiltersToState()
            return;
        }

        if (urlPath === persistedSearchPage.urlPath) {
            setFilters(prevState => persistedSearchFilters);
            setMortgageAdviser(calculateIfMortgageAdviserSearch(persistedSearchFilters.parentList))
        }
        addUrlParamSearchFiltersToState()
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        }
    }, []);

    useEffect(() => {
        handlePersistState()
        props.handleSubmit(filters)
    }, [filters]);

    useEffect(() => {
        if (didMountRef.current) {
            setFilters(prevState => {
                return {
                    ...prevState,
                    parentList: [],
                    servicesList: []
                }
            })
        } else didMountRef.current = true

    }, [type, attribute_id]);

    function handleWindowSizeChange() {
        setWidth(window.innerWidth);
    }

    const addUrlParamSearchFiltersToState = () => {
        //we can have a number of params in the url that will establish the value of independent, advanced qualifications and transparent fees filters
        const params = props.searchFilterUrlParams
        const paramKeys = Object.keys(params)
        if (paramKeys.length) {
            const newFilters = {}
            paramKeys.map(filterName => newFilters[filterName] = parseInt(params[filterName]) === 1 )
            setFilters((prevState)=> {
                return {
                    ...prevState,
                    ...newFilters
                }
            })
        }

    }

    const getValue = (item, element) => {
        switch (item) {
            case 'search_range':
            case 'gender':
                return element.value
            case 'wealth':
                return element.id
            case 'parentList':
            case 'servicesList':
                return element
            default:
                return element.target.value
        }
    }

    const handleChange = item => e => {
        const value = getValue(item, e);

        const nextMortgageAdviserValue = calculateIfMortgageAdviserSearch(value);
        let newFilters = {...filters}

        if (item === 'parentList') {
            if (value.includes(FAKE_FINANCIAL_ADVISER_PENSION_TRANSFERS_DROPDOWN_ID)) {
                return setShowSearchBarPopover(!showSearchBarPopover);
            }

            if (mortgageAdviser !== nextMortgageAdviserValue) {
                newFilters = {...newFilters, ...calculateMortgageAdviserFilterState(nextMortgageAdviserValue)}
                setMortgageAdviser(nextMortgageAdviserValue)
            }

            newFilters = {...newFilters, type_filter_value: getSearchFilterTypeValueByService(value)}
        }

        if (item === 'wealth') {
            newFilters = {...newFilters, wealth_level_ids: getWealthLevelIds(value, mortgageAdviser)}
        }

        newFilters = {...newFilters, [item]: value}
        setFilters({...newFilters})
        searchFiltersGTM(item, newFilters, mortgageAdviser);
    }

    const handleSwitch = item => e => {
        searchFiltersCheckboxGTM(item, !filters[item])
        setFilters({
            ...filters,
            [item]: !filters[item]
        })
        searchFiltersCheckboxGTM(item)
    }

    function toggleMobileFilters() {
        setMobileFilters(!mobileFilters)
    }

    const handleDeleteServices = id => e => {
        setFilters({
            ...filters,
            servicesList: filters.servicesList.filter(arrayItem => arrayItem !== id )
        })
    }

    const handleDeleteParent = id => e => {
        const { servicesList } = filters;
        let temporaryServiceList = [...servicesList];
        const parentObject = find(services[type], (parent) => parent.id === id)
        const parentList = filters.parentList.filter(arrayItem => arrayItem !== id);
        const nextMortgageAdviserValue = calculateIfMortgageAdviserSearch(parentList);

        forEach(parentObject.children, function(child) {
            temporaryServiceList = temporaryServiceList.filter(arrayItem => arrayItem === child.id )
        });

        let newFilters = {
            ...filters,
            servicesList: temporaryServiceList,
            parentList: parentList,
            type_filter_value: getSearchFilterTypeValueByService(parentList)
        }

        if (mortgageAdviser !== nextMortgageAdviserValue) {
            newFilters = {...newFilters, ...calculateMortgageAdviserFilterState(nextMortgageAdviserValue)};
            setMortgageAdviser(nextMortgageAdviserValue)
        }

        setFilters({...newFilters})
    }

    const handleCancel = e => {
        e.preventDefault();

        setFilters({
            ...filters,
            search_range: 5,
            is_contactable: false,
            promotion: false,
            servicesList: [],
        });
        setShowFilters(false)
        setMobileFilters(false)
    }

    function handleSubmitSearchBarPopover(value) {
        const { parentList } = filters;

        if (parentList.includes(value)) return;

        const newParentList = getParentListStateForPensionTransfersService(value, parentList);
        const newFilters = {
            ...filters,
            parentList: [...newParentList]
        }
        setFilters({...newFilters})
        searchFiltersGTM('parentList', newFilters, mortgageAdviser);
    }

    const handlePersistState = () => {
        setPersistedState(PERSISTED_STATE_SEARCH_FILTERS, filters);
    }

    function getServicesDropdownProps() {
        return {
            services: services,
            showAdviceAreas: showAdviceAreas,
            defaultSelection: [...filters.parentList],
            type: type,
            id: createInputId('services'),
            options: [...getDropdownOptionsByVertical(type)],
            optionValueKey: 'id',
            optionsToFilterResultsFrom: [...getDropdownOptionsByVertical(type, false)],
            placeholder: `I am looking for help with...`,
            highlight: true,
            isDefaultOpen: true,
            onChange: (e) => handleChange('parentList')(e),
            onDelete: (e) => handleDeleteParent(e),
        }
    }

    //filter components
    const renderSectionTitle = () => (
        <div className={'p-2 grow-0 basis-[100%] max-w-[100%] m-0 box-border'}>
            <p className={'text-secondary-blue font-semibold text-[1.1rem] leading-6 m-0'}>Filter results:</p>
        </div>
    )

    const renderServicesDropdown = () => (
        <div
            className={`basis-full max-w-full ${(showAdviceAreas && filters.parentList.length > 0) ? 'md:basis-1/3 md:max-w-[33.3333333333%]' : showAdviceAreas ? 'md:basis-2/3 md:max-w-2/3' : 'md:basis-1/2 md:max-w-1/2'} flex flex-col justify-start p-2 grow-0  m-0 box-border`}
            id={SEARCH_FILTER_SERVICES_WRAPPER}
        >
            <p className={'mx-0 mt-0 mb-[0.35em] text-[1.1rem] font-normal leading-6  overflow-hidden text-nowrap text-ellipsis'}>
                <b> { showAdviceAreas ? 'Advice Areas' : 'Services' }</b>
            </p>
            <div className={'relative'}>
                <FilterServicesDropdown {...getServicesDropdownProps()} />
                <SearchBarPopover
                    visible={showSearchBarPopover}
                    onClose={() => setShowSearchBarPopover(false)}
                    onSubmit={handleSubmitSearchBarPopover}
                />
            </div>
        </div>
    )

    const renderServiceDetailsDropdown = () => {
        const servicesLabels = getServicesLabels(services[type], filters.parentList);
        const options = (filters.parentList.length && showAdviceAreas)
            ? getChildDropdownOptions(services[type], filters.parentList)
            : []
        const dropdownProps =  {
                services: services,
                showAdviceAreas: showAdviceAreas,
                defaultSelection: [...filters.servicesList],
                type: type,
                id: createInputId('advice_areas'),
                options: options,
                optionValueKey: 'id',
                optionsToFilterResultsFrom: options,
                placeholder: 'Select advice areas',
                onChange: (e) => handleChange('servicesList')(e),
                    onDelete: (e) => handleDeleteServices(e),
                }

        return (
            (showAdviceAreas && filters.parentList.length > 0) &&
                <div className={'flex flex-col justify-start p-2 grow-0 basis-full max-w-full sm:basis-5/12 sm:max-w-[41.6666666667%] md:basis-1/3 md:max-w-[33.3333333333%] m-0 box-border'} >
                    <p className={'mx-0 mt-0 mb-[0.35em] text-[1.1rem] font-normal leading-6 overflow-hidden text-nowrap text-ellipsis'}>
                        <b>
                            {
                                servicesLabels
                                    ? servicesLabels
                                    : 'Select Your Advice Areas'
                            }
                        </b>
                    </p>
                    <FilterServicesDropdown {...dropdownProps} />
                    {
                        !filters.servicesList.length &&
                        <p className={'mx-3 mt-1 mb-0 text-xs font-normal leading-6 text-cherry-red'}>
                            Please select an advice area for your changes to take effect
                        </p>
                    }
                </div>
        )
    }

    const renderMobileButtons = () => (
        <div className={`m-0 w-[100%] flex box-border`} id={"buttonRowOne"}>
            <div className={'p-2 grow-0 basis-[50%] max-w-[50%] m-0 box-border'}>
                <Button type={'button'} className={'w-full [&_span]:text-pale-grass-green bg-mint-green hover:shadow-lg hover:bg-grass-green'} onClick={handleCancel} text={'Cancel'} />
            </div>
            <div className={'p-2 grow-0 basis-[50%] max-w-[50%] m-0 box-border'}>
                <Button type={'button'} className={'w-full [&_span]:text-white bg-primary-blue hover:shadow-lg hover:bg-dark-warm-grey'} onClick={toggleMobileFilters} text={'Update'} />
            </div>
        </div>

    )

    const renderShowMoreFiltersButton = (isMobile) => {

        const getButtonLabel = () => {

            if (type === FINANCIAL_ADVISER_ID) {
                if (!isMobile && showFilters) {
                    return 'Hide more filters'
                }
                if (!isMobile && !showFilters) {
                    return 'Show more filters'
                }
                if (isMobile && mobileFilters) {
                    return 'Hide more filters'
                }
                if (isMobile && !mobileFilters) {
                    return 'Show more filters'
                }
            }

            if (type !== FINANCIAL_ADVISER_ID) {
                if (!isMobile && showFilters) {
                    return 'Hide more filters'
                }
                if (!isMobile && !showFilters) {
                    return 'Show more filters'
                }
                if (isMobile && mobileFilters) {
                    return 'Hide filters'
                }
                if (isMobile && !mobileFilters) {
                    return 'Show filters'
                }
            }
        }

        return(
            <div
                className={'flex flex-col justify-start p-2 grow-0 basis-auto max-w-auto md:basis-2/12 md:max-w-[16.6666666667%] m-0 box-border'}
            >
                <Button
                    type={'button'}
                    className={'mx-0 my-0 md:mt-6 md:mx-0 md:mb-0 h-14 bg-transparent hover:bg-transparent no-highlights shadow-none [&_span]:text-secondary-blue p-0 text-sm'}
                    onClick={() => {
                        if (isMobile) toggleMobileFilters()
                        if (!isMobile) setShowFilters(!showFilters)
                    }}
                    text={<><Tune /><b>{getButtonLabel()}</b></>}
                />
            </div>
        )
    }

    const renderWealthLevelDropdown = () => {

        if (showAdviceAreas) return null

        const { wealth} = filters;
        const wealthDropdownOptions = getWealthDropdownOptions(mortgageAdviser);

        return (
            <div className={'flex flex-col justify-start p-2 grow-0 basis-full max-w-full md:basis-2/12 md:max-w-[16.6666666667%] m-0 box-border'}>
                <p className={'mx-0 mt-0 mb-[0.35em] text-[1.1rem] font-normal leading-6 overflow-hidden text-nowrap text-ellipsis'}>
                    <b>{ mortgageAdviser ? 'Mortgage value' : 'Savings/Pensions Level' }</b>
                </p>
                <Select
                    className={'h-11'}
                    defaultSelection={wealth ? wealthDropdownOptions.find(o => o.id === wealth) : wealthDropdownOptions[0]}
                    onSelection={handleChange('wealth')}
                    id={createInputId('wealth')}
                    options={wealthDropdownOptions}
                    optionValueKey={'id'}
                    renderOption={(option, isSelected) => (<SelectOption label={option.label} isSelected={isSelected} />)}
                />
            </div>
        )
    }

    const renderDistanceDropdown = () => {
        const {
            search_range,
        } = filters;

        return (
            <div
                className={'flex max-md:flex-col justify-start p-2 grow-0 basis-full max-w-full md:basis-1/4 md:max-w-[25%] m-0 box-border '}>
                    <p className={'text-[14px] mx-0  pr-2 max-md:mt-0 mb-[0.35em] max-md:text-[1.1rem] max-md:font-normal max-md:leading-6'}>
                    <b>Distance</b>
                </p>
                <Select
                    className={'h-11 w-[80%] max-md:w-full'}
                    defaultSelection={search_range ? searchRangeOptions.find(sr => sr.value === search_range) : searchRangeOptions[0]}
                    onSelection={handleChange('search_range')}
                    id={createInputId('search_range')}
                    options={searchRangeOptions}
                    renderOption={(option, isSelected) => (<SelectOption label={option.label} isSelected={isSelected} />)}
                />
            </div>
        )
    }

    const renderGenderDropdown = () => {
        const {
            gender,
        } = filters;
        
        return (
            <div
                className={'flex flex-col justify-start p-2 grow-0 basis-full max-w-full md:basis-2/12 md:max-w-[16.6666666667%] m-0 box-border'}>
                <p className={'mx-0 mt-0 mb-[0.35em] text-[1.1rem] font-normal leading-6 overflow-hidden text-nowrap text-ellipsis'}>
                    <b>Adviser Gender</b>
                </p>
                <Select
                    className={'h-11'}
                    defaultSelection={gender ? genderOptions.find(sg => sg.value === gender) : genderOptions[0]}
                    onSelection={handleChange('gender')}
                    id={createInputId('gender')}
                    options={genderOptions}
                    renderOption={(option, isSelected) => (<SelectOption label={option.label} isSelected={isSelected} />)}
                />
            </div>
        )
    }

    const renderMoreFilters = () => {
        return (
            <>
                {renderDistanceDropdown()}
                <div className={'flex justify-between p-2 grow-0 basis-full max-w-full md:basis-1/4 md:max-w-[25%] m-0 box-border'}>
                    <Checkbox
                        label="Only show advisers who are verified and available for contact"
                        checked={filters.is_contactable}
                        color="secondary"
                        id={createInputId('is_contactable')}
                        onChange={handleSwitch('is_contactable')}
                    />
                </div>
                <div className={'flex justify-between p-2 grow-0 basis-full max-w-full md:basis-1/4 md:max-w-[25%] m-0 box-border'}>
                    <Checkbox
                        label="Only show advisers with promotional offers"
                        color="secondary"
                        checked={filters.promotion}
                        id={createInputId('promotion')}
                        onChange={handleSwitch('promotion')}
                    />
                </div>
                <div className={'flex justify-between p-2 grow-0 basis-full max-w-full md:basis-1/4 md:max-w-[25%] m-0 box-border'}>
                    <Checkbox
                        label="Include professionals further away who will travel to you"
                        color="secondary"
                        checked={filters.include_travel_professionals}
                        id={createInputId('include_travel_professionals')}
                        onChange={handleSwitch('include_travel_professionals')}
                    />
                </div>
                { type === FINANCIAL_ADVISER_ID &&
                    <>
                        <div className={'flex justify-between md:justify-start p-2 grow-0 basis-full max-w-full md:basis-1/4 md:max-w-[25%] m-0 box-border'}>
                            <Checkbox
                                label='Show only independent advisers'
                                color="secondary"
                                checked={filters.type_filter}
                                id={createInputId('type_filter')}
                                onChange={handleSwitch('type_filter')}
                                tooltip={<Tooltip
                                    fill="#C1B8B3"
                                    size={'1.25rem'}
                                    content={filters.type_filter_value !== 'ifa' ? 'An independent mortgage adviser can advise across a wide range of mortgage products in the market. It is important to ask the mortgage adviser whether any limitation exists in the range of products that it will offer. For example, does the mortgage adviser check lenders’ direct-only deals.' : 'An independent adviser can advise across a sufficiently diverse range of products to ensure that the client’s objectives can be suitably met. They are in no way limited to products provided by companies they work for'}
                                />}
                            />
                        </div>
                        <div className={'flex justify-between md:justify-start p-2 grow-0 basis-full max-w-full md:basis-1/4 md:max-w-[25%] m-0 box-border'}>
                            <Checkbox
                                label="Only show those with advanced qualifications"
                                color="secondary"
                                checked={filters.advanced_qualifications}
                                id={createInputId('advanced_qualifications')}
                                onChange={handleSwitch('advanced_qualifications')}
                                tooltip={<Tooltip
                                    content="All Financial Advisers are required to hold a level 4 qualification in financial advice recognised by the Financial Conduct Authority. Some advisers have also achieved higher level advanced qualifications, such as Chartered status"
                                    fill="#C1B8B3"
                                    size={'1.25rem'}
                                />}
                            />
                        </div>
                        <div className={'flex justify-between  md:justify-start p-2 grow-0 basis-full max-w-full md:basis-1/4 md:max-w-[25%] m-0 box-border'}>
                            <Checkbox
                                label="Only show fixed fee advisers"
                                color="secondary"
                                checked={filters.fixed_fees}
                                id={createInputId('fixed_fees')}
                                onChange={handleSwitch('fixed_fees')}
                                tooltip={<Tooltip
                                    content="A fixed fee adviser charges a set price to undertake a specific piece of work."
                                    fill="#C1B8B3"
                                    size={'1.25rem'}
                                />}
                            />
                        </div>
                    </>
                }
            </>
        )
    }

    //mobile and desktop filter lists
    const renderFiltersMobile = () => {
        return (
            <div className={'-mx-2 -mt-2 mb-2 w-[calc(100% + 16px)] flex flex-wrap box-border'} id="filtersWrapper">
                {renderSectionTitle()}
                {renderMobileButtons()}
                {type !== FINANCIAL_ADVISER_ID && renderServicesDropdown()}
                {renderServiceDetailsDropdown()}
                {renderWealthLevelDropdown()}
                {renderGenderDropdown()}
                {renderMoreFilters()}
            </div>
        )
    }

    const renderFilters = () => {
        return (
            <>
                {renderServicesDropdown()}
                {renderServiceDetailsDropdown()}
                {renderWealthLevelDropdown()}
                {renderGenderDropdown()}
                {renderShowMoreFiltersButton(false)}
                {showFilters && renderMoreFilters()}
            </>
        );
    }

    const showDrawer = () => {
        return mobileFilters && (width < 1279.95);
    }

    //mobile and desktop render
    const renderMobile = () => {
        return (
            <>
                <div className={'my-4 mx-0 flex flex-wrap box-border'}>
                    {renderSectionTitle()}
                    {renderShowMoreFiltersButton(true)}
                    {type === FINANCIAL_ADVISER_ID && renderServicesDropdown()}
                </div>
                <Drawer isOpen={showDrawer()}>{ renderFiltersMobile() }</Drawer>
            </>
        )
    }

    const renderDesktop = () => {
        return (
            <div className={'-mx-2 -mt-2 mb-2 w-[calc(100% + 16px)] flex flex-wrap box-border'} id="filtersWrapper">
                {renderSectionTitle()}
                {renderFilters()}
            </div>
        )
    }

    return (
        <>
            <div data-test={"mobile-wrapper"} className={'block md:hidden'}>{renderMobile()}</div>
            <div data-test={"desktop-wrapper"} className={'hidden md:block'}>{renderDesktop()}</div>
        </>
    );
}

SearchFilters.propTypes = propTypes;

export default SearchFilters;
